import { diaNegocio, formatearMoneda, type PromocionVista } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { fecha } from '@/lib/utils';
import { useServicios } from '../agenda/datos';
import { avisarError } from '@/lib/avisos';

const TIPOS = { porcentaje: 'Porcentaje', monto: 'Monto fijo', paquete: 'Paquete' } as const;
const ESTADO = {
  propuesta: { texto: 'Por autorizar', tono: 'aviso' },
  autorizada: { texto: 'Autorizada', tono: 'exito' },
  rechazada: { texto: 'Rechazada', tono: 'peligro' },
  expirada: { texto: 'Finalizada', tono: 'neutro' },
} as const;

function valorTexto(p: Pick<PromocionVista, 'tipo' | 'valor'>) {
  return p.tipo === 'porcentaje' ? `${p.valor / 100}%` : p.tipo === 'monto' ? `${formatearMoneda(p.valor)} por pieza` : formatearMoneda(p.valor);
}

function NuevaPromocion({ onCerrar }: { onCerrar: () => void }) {
  const qc = useQueryClient();
  const { puede } = useSesion();
  const servicios = useServicios();
  const hoy = diaNegocio(new Date());
  const [d, setD] = useState({ nombre: '', descripcion: '', tipo: 'porcentaje' as keyof typeof TIPOS, valor: '', servicioIds: [] as string[], vigenteDesde: hoy, vigenteHasta: '' });
  const guardar = useMutation({
    mutationFn: () =>
      api<PromocionVista>('/promociones', {
        method: 'POST',
        body: {
          nombre: d.nombre,
          descripcion: d.descripcion || undefined,
          tipo: d.tipo,
          valor: Math.round(Number.parseFloat(d.valor) * 100),
          servicioIds: d.servicioIds,
          productoIds: [],
          vigenteDesde: d.vigenteDesde,
          vigenteHasta: d.vigenteHasta || undefined,
        },
      }),
    onSuccess: (p) => {
      toast.success(p.estado === 'autorizada' ? 'Promoción creada y activa' : 'Promoción enviada al dueño para su autorización');
      void qc.invalidateQueries({ queryKey: ['promociones'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });
  const alternar = (id: string) => setD({ ...d, servicioIds: d.servicioIds.includes(id) ? d.servicioIds.filter((x) => x !== id) : [...d.servicioIds, id] });
  const valido = d.nombre.trim().length >= 2 && Number(d.valor) > 0 && (d.tipo !== 'paquete' || d.servicioIds.length >= 2);

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Nueva promoción"
      descripcion={puede('promociones.autorizar') ? 'Quedará activa de inmediato.' : 'Se enviará al dueño; se podrá usar cuando la autorice.'}
      pie={
        <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={!valido}>
          Guardar
        </Button>
      }
    >
      <div className="grid gap-3">
        <Campo etiqueta="Nombre" requerido>{(p) => <Input {...p} value={d.nombre} maxLength={80} onChange={(e) => setD({ ...d, nombre: e.target.value })} />}</Campo>
        <div className="grid grid-cols-2 gap-2">
          <Campo etiqueta="Tipo">
            {(p) => (
              <Select {...p} value={d.tipo} onChange={(e) => setD({ ...d, tipo: e.target.value as keyof typeof TIPOS })}>
                {Object.entries(TIPOS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo etiqueta={d.tipo === 'porcentaje' ? 'Descuento (%)' : d.tipo === 'monto' ? 'Descuento ($ por pieza)' : 'Precio del paquete ($)'} requerido>
            {(p) => <Input {...p} type="number" min="0" step="0.01" className="cifras" value={d.valor} onChange={(e) => setD({ ...d, valor: e.target.value })} />}
          </Campo>
        </div>
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium">
            {d.tipo === 'paquete' ? 'Servicios del paquete (mínimo 2)' : 'Servicios a los que aplica (ninguno = solo productos)'}
          </legend>
          <div className="grid max-h-44 gap-1 overflow-y-auto rounded-md border border-border p-2 sm:grid-cols-2">
            {servicios.data?.map((s) => (
              <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[13.5px] hover:bg-muted">
                <input type="checkbox" className="accent-[var(--bb-accent)]" checked={d.servicioIds.includes(s.id)} onChange={() => alternar(s.id)} />
                <span className="flex-1">{s.nombre}</span>
                <span className="cifras text-[12px] text-foreground-muted">{formatearMoneda(s.precio)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-2 gap-2">
          <Campo etiqueta="Desde">{(p) => <Input {...p} type="date" value={d.vigenteDesde} onChange={(e) => setD({ ...d, vigenteDesde: e.target.value })} />}</Campo>
          <Campo etiqueta="Hasta" ayuda="Vacío = sin fecha de fin">
            {(p) => <Input {...p} type="date" min={d.vigenteDesde} value={d.vigenteHasta} onChange={(e) => setD({ ...d, vigenteHasta: e.target.value })} />}
          </Campo>
        </div>
        <Campo etiqueta="Descripción">{(p) => <Textarea {...p} value={d.descripcion} maxLength={300} onChange={(e) => setD({ ...d, descripcion: e.target.value })} />}</Campo>
      </div>
    </Dialogo>
  );
}

export function PromocionesPage() {
  const { puede } = useSesion();
  const qc = useQueryClient();
  const [nueva, setNueva] = useState(false);
  const lista = useQuery({ queryKey: ['promociones'], queryFn: () => api<PromocionVista[]>('/promociones') });
  const servicios = useServicios(false);
  const finalizar = useMutation({
    mutationFn: (id: string) => api(`/promociones/${id}/finalizar`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Promoción finalizada');
      void qc.invalidateQueries({ queryKey: ['promociones'] });
    },
    onError: (e) => avisarError(e),
  });
  const nombres = (ids: string[]) => ids.map((id) => servicios.data?.find((s) => s.id === id)?.nombre ?? '—').join(' + ');

  return (
    <ModuleLayout titulo="Caja" secciones={SECCIONES_POR_MODULO.caja}>
      <EncabezadoPagina
        titulo="Promociones"
        descripcion="Descuentos por porcentaje, monto fijo o paquetes de servicios. Las que propone recepción requieren autorización del dueño."
        acciones={
          (puede('promociones.proponer') || puede('promociones.autorizar')) && (
            <Button onClick={() => setNueva(true)}>
              <Plus /> Nueva promoción
            </Button>
          )
        }
      />
      <Tarjeta className="overflow-x-auto">
        {lista.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !lista.data?.length ? (
          <Vacio icono={<Tag />} titulo="Sin promociones" descripcion="Crea la primera para aplicarla desde el carrito." />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Promoción</Th>
                <Th>Tipo</Th>
                <Th className="text-right">Valor</Th>
                <Th>Aplica a</Th>
                <Th>Vigencia</Th>
                <Th>Estado</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {lista.data.map((p) => (
                <Tr key={p.id}>
                  <Td>
                    <span className="font-medium">{p.nombre}</span>
                    {p.descripcion && <span className="block text-[12.5px] text-foreground-muted">{p.descripcion}</span>}
                  </Td>
                  <Td>{TIPOS[p.tipo]}</Td>
                  <Td className="cifras whitespace-nowrap text-right">{valorTexto(p)}</Td>
                  <Td className="text-[13px]">{p.servicioIds.length ? nombres(p.servicioIds) : 'Productos'}</Td>
                  <Td className="cifras whitespace-nowrap text-[13px]">
                    {fecha(`${p.vigenteDesde}T12:00:00-06:00`)} – {p.vigenteHasta ? fecha(`${p.vigenteHasta}T12:00:00-06:00`) : 'sin fin'}
                  </Td>
                  <Td>
                    <Insignia tono={ESTADO[p.estado].tono}>{ESTADO[p.estado].texto}</Insignia>
                    {p.estado === 'autorizada' && !p.vigente && <span className="ml-1 text-[12px] text-foreground-muted">(fuera de fechas)</span>}
                  </Td>
                  <Td className="text-right">
                    {puede('promociones.autorizar') && (p.estado === 'autorizada' || p.estado === 'propuesta') && (
                      <Button variante="fantasma" tamano="sm" onClick={() => finalizar.mutate(p.id)}>
                        Finalizar
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {nueva && <NuevaPromocion onCerrar={() => setNueva(false)} />}
    </ModuleLayout>
  );
}
